import { isBoolean } from 'lodash';

import { getRegisteredClass, registerClass } from '../SatCoreRegistry';

import Auth from '../managers/AuthManager';
import accommodationsManager from '../managers/AccommodationsManager';
import adminUsersManager from '../managers/AdminUsersManager';
import appManager from '../managers/AppManager';
import assignmentManager from '../managers/AssignmentManager';
import basicIframeModalLauncherManager from '../managers/BasicIframeModalLauncherManager';
import classroomManager from '../managers/ClassroomManager';
import contentManager from '../managers/ContentManager';
import contextHelpManager from '../managers/ContextHelpManager';
import courseDeliveryManager from '../managers/CourseDeliveryManager';
import courseManager from '../managers/CourseManager';
import dialogManager from '../managers/DialogManager';
import dynamicSatelliteManager from '../managers/DynamicSatelliteManager';
import googleClassroomManager from '../managers/GoogleClassroomManager';
import gradebookManager from '../managers/GradebookManager';
import libraryManager from '../managers/LibraryManager';
import reportContextManager from '../managers/reports/ReportContextManager';
import reportOverrideManager from '../managers/reports/ReportOverrideManager';
import studentContentCardManager from '../managers/StudentContentCardManager';
import studentCourseDrivenExperienceManager from '../managers/StudentCourseDrivenExperienceManager';
import studentProgressManager from '../managers/StudentProgressManager';
import thirdPartyManager from '../managers/ThirdPartyManager';
import topNavManager from '../managers/TopNavManager';
import userManager from '../managers/UserManager';

import {
  getCssUrlArgument, getSessionStorageItem, getStyleVar, isWhiteStyleColor, setSessionStorageItem
} from '../utils';

import { addSatellite as i18nAddSatellite } from '../i18n';

export default class DynamicSatelliteService {
  static fetchDynamicSatelliteByDomain = async () => {
    try {
      const InitService = getRegisteredClass('InitService');

      const dynamicSatelliteResponseData = await dynamicSatelliteManager.fetchDynamicSatelliteByDomain();

      const {
        reportPrefix, satelliteCode, satelliteCssUrl, satelliteJsonUrl, satelliteLayoutUrl, satelliteName
      } = dynamicSatelliteResponseData;

      // TODO - useful for local development if you use HTTPS localhost; change the above 'const' declaration to 'let' and
      // - set these values to the files you copy to react-sales-demo public folder for the dynamic satellite
      // - you want to use.
      // satelliteCssUrl = '/demo.css';
      // satelliteJsonUrl = '/demoTranslations.json';
      // satelliteLayoutUrl = '/demoSatelliteLayout.json';

      Auth.setPublisherSatelliteCode(satelliteCode);
      userManager.setClientPrefix(reportPrefix);
      setSessionStorageItem('dynamicPublisherSatelliteCode', satelliteCode);

      // TODO useful for local development if you have a lot of Sirius data
      // TODO ---
      // TODO remember to comment back out before committing any changes
      // Auth.setPublisherSatelliteCode('SIRIUS_SATELLITE');
      // userManager.setClientPrefix('SIRIUS');
      // setSessionStorageItem('dynamicPublisherSatelliteCode', 'SIRIUS_SATELLITE');

      // TODO remove
      // console.info('dynamicSatelliteResponseData', dynamicSatelliteResponseData);

      // TODO remove
      // console.info('satelliteCssUrl', satelliteCssUrl);

      document.title = satelliteName;
      appManager.setSatelliteName(satelliteName);

      const dynamicSatelliteLayoutConfig = await this.initDynamicSatelliteLayoutConfig({ satelliteLayoutUrl });

      await this.initDynamicSatelliteFlagOverrides(dynamicSatelliteLayoutConfig);

      await this.initDynamicSatelliteTranslations({ satelliteJsonUrl });

      this.initDynamicSatelliteStyleSheet({ satelliteCssUrl });

      setTimeout(() => {
        this.initDynamicFavIconIfApplicable();
        InitService.initLocale();
      }, 500);
    } catch (error) {
      console.error(error);
    }
  }

  static initDynamicSatelliteLayoutConfig = async ({ satelliteLayoutUrl } = {}) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    const dynamicLayoutResponse = satelliteLayoutUrl ? await fetch(satelliteLayoutUrl, {
      headers,
      method: 'GET',
      redirect: 'follow'
    }) : undefined;

    const dynamicSatelliteLayoutConfig = await dynamicLayoutResponse?.json?.();
    dynamicSatelliteManager.setDynamicSatelliteLayoutConfig(dynamicSatelliteLayoutConfig);

    return dynamicSatelliteLayoutConfig;
  }

  /**
   * Initialize dynamic satellite override flags that we would typically set in an `index.js` file
   * for a given non-dynamic satellite.
   *
   * Note this method is intended to be used for **dynamic satellites only**.
   *
   * The `dynamicSatelliteLayoutConfig.satelliteIndexFlagOverrides` object will be looked at
   * to determine which flag overrides should be set.
   */
  static initDynamicSatelliteFlagOverrides = async (dynamicSatelliteLayoutConfig) => {
    const flagOverrides = dynamicSatelliteLayoutConfig?.satelliteIndexFlagOverrides;
    if (!flagOverrides || typeof flagOverrides !== 'object') {
      return;
    }

    const {
      additionalAdminTopNavExternalResources,
      additionalStudentTopNavExternalResources,
      additionalTeacherTopNavExternalResources,
      allowAdminUsersAddNewTeacher,
      allowClassroomCourseCardDragDropReorder,
      allowContentItemDescriptionIcon,
      allowDigitalItemBanking,
      allowFetchLibraryDataIfNoLicensedBanks,
      allowGoogleClassroomIntegration,
      allowGradebookStandards,
      allowObservationalGrader,
      allowPresentationMode,
      allowPreserveCourseElementResourceScrollPosition,
      allowPrintItemBanking,
      allowReportHideColumnsResetAllAsUnselectedAction,
      allowReportHideColumnsResetAllToOriginalSelectionsAction,
      allowReportHideRowsResetAllAsUnselectedAction,
      allowReportHideRowsResetAllToOriginalSelectionsAction,
      allowReportStandards,
      allowRibbonTOC,
      allowStudentBooks,
      allowStudentCourseDrivenAssignmentFiltering,
      allowStudentCourseDrivenCarousel,
      allowStudentCourseDrivenExperience,
      allowTeacherClassroomHeaderCustomActionButtons,
      allowedStudentDashboardProgressWidgets,
      allowedStudentProgressWidgets,
      allowedThirdPartyChatLauncherUserRoles,
      alwaysExcludeStudentAssignmentCardAlignmentPills,
      avatarPickerFacultyName,
      avatarPickerStudentName,
      basicIframeModalLauncherUrlMapEntries,
      classCardInfoOrderArray,
      classWorkspaceFeature,
      courseTreeLeafButtonTypeOrderArray,
      customEndpointsForGoogleClassroom,
      customEndpointsForUser,
      includeAlignments,
      includeClassroomAccommodations,
      includeGroups,
      isUserProfileReadOnly,
      maxReportFacultyScoreCellsPerPage,
      reportContextTypeDefaultOrderArray,
      restrictAvatar,
      restrictAvatarForAllUserTypes,
      showGradebookKeyViewMoreModal,
      showResourcePacingToolbar,
      studentAssignmentMenuStatusOrderArray,
      studentContentCardDateFormatStrategy,
      teacherClassroomCardFooterNavButtonOrderArray,
      testBuilderContextHelpId,
      thirdPartyChatLauncherCustomVariables,
      thirdPartyChatLauncherUrl,
      useProductLineLibrary,
      wordToolsTTSDependent
    } = flagOverrides;

    if (Array.isArray(additionalAdminTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('admin', additionalAdminTopNavExternalResources);
    }
    if (Array.isArray(additionalStudentTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('student', additionalStudentTopNavExternalResources);
    }
    if (Array.isArray(additionalTeacherTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('teacher', additionalTeacherTopNavExternalResources);
    }
    if (isBoolean(allowAdminUsersAddNewTeacher)) {
      adminUsersManager.setAllowAdminUsersAddNewTeacher(allowAdminUsersAddNewTeacher);
    }
    if (isBoolean(allowClassroomCourseCardDragDropReorder)) {
      courseManager.setAllowClassroomCourseCardDragDropReorder(allowClassroomCourseCardDragDropReorder);
    }
    if (isBoolean(allowContentItemDescriptionIcon)) {
      courseDeliveryManager.setAllowContentItemDescriptionIcon(allowContentItemDescriptionIcon);
    }
    if (isBoolean(allowDigitalItemBanking)) {
      libraryManager.setAllowDigitalItemBanking(allowDigitalItemBanking);
    }
    if (isBoolean(allowFetchLibraryDataIfNoLicensedBanks)) {
      libraryManager.setAllowFetchLibraryDataIfNoLicensedBanks(allowFetchLibraryDataIfNoLicensedBanks);
    }
    if (isBoolean(allowGoogleClassroomIntegration)) {
      googleClassroomManager.setAllowGoogleClassroomIntegration(allowGoogleClassroomIntegration);
    }
    if (isBoolean(allowGradebookStandards)) {
      gradebookManager.setAllowGradebookStandards(allowGradebookStandards);
    }
    if (isBoolean(allowObservationalGrader)) {
      gradebookManager.setAllowObservationalGrader(allowObservationalGrader);
    }
    if (isBoolean(allowPresentationMode)) {
      assignmentManager.setAllowPresentationMode(allowPresentationMode);
    }
    if (isBoolean(allowPreserveCourseElementResourceScrollPosition)) {
      courseManager.setAllowPreserveCourseElementResourceScrollPosition(allowPreserveCourseElementResourceScrollPosition);
    }
    if (isBoolean(allowPrintItemBanking)) {
      libraryManager.setAllowPrintItemBanking(allowPrintItemBanking);
    }
    if (isBoolean(allowReportHideColumnsResetAllAsUnselectedAction)) {
      reportOverrideManager.setAllowReportHideColumnsResetAllAsUnselectedAction(
        allowReportHideColumnsResetAllAsUnselectedAction
      );
    }
    if (isBoolean(allowReportHideColumnsResetAllToOriginalSelectionsAction)) {
      reportOverrideManager.setAllowReportHideColumnsResetAllToOriginalSelectionsAction(
        allowReportHideColumnsResetAllToOriginalSelectionsAction
      );
    }
    if (isBoolean(allowReportHideRowsResetAllAsUnselectedAction)) {
      reportOverrideManager.setAllowReportHideRowsResetAllAsUnselectedAction(
        allowReportHideRowsResetAllAsUnselectedAction
      );
    }
    if (isBoolean(allowReportHideRowsResetAllToOriginalSelectionsAction)) {
      reportOverrideManager.setAllowReportHideRowsResetAllToOriginalSelectionsAction(
        allowReportHideRowsResetAllToOriginalSelectionsAction
      );
    }
    if (isBoolean(allowReportStandards)) {
      reportOverrideManager.setAllowReportStandards(allowReportStandards);
    }
    if (isBoolean(allowRibbonTOC)) {
      courseManager.setAllowRibbonTOC(allowRibbonTOC);
    }
    if (isBoolean(allowStudentBooks)) {
      courseManager.setAllowStudentBooks(allowStudentBooks);
    }
    if (isBoolean(allowStudentCourseDrivenAssignmentFiltering)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenAssignmentFiltering(allowStudentCourseDrivenAssignmentFiltering);
    }
    if (isBoolean(allowStudentCourseDrivenCarousel)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenCarousel(allowStudentCourseDrivenCarousel);
    }
    if (isBoolean(allowStudentCourseDrivenExperience)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenExperience(allowStudentCourseDrivenExperience);
    }
    if (isBoolean(allowTeacherClassroomHeaderCustomActionButtons)) {
      classroomManager.setAllowTeacherClassroomHeaderCustomActionButtons(allowTeacherClassroomHeaderCustomActionButtons);
    }
    if (Array.isArray(allowedStudentDashboardProgressWidgets)) {
      studentProgressManager.setAllowedStudentDashboardProgressWidgets(allowedStudentDashboardProgressWidgets);
    }
    if (Array.isArray(allowedStudentProgressWidgets)) {
      studentProgressManager.setAllowedStudentProgressWidgets(allowedStudentProgressWidgets);
    }
    if (Array.isArray(allowedThirdPartyChatLauncherUserRoles)) {
      thirdPartyManager.setAllowedThirdPartyChatLauncherUserRoles(allowedThirdPartyChatLauncherUserRoles);
    }
    if (isBoolean(alwaysExcludeStudentAssignmentCardAlignmentPills)) {
      assignmentManager.setAlwaysExcludeStudentAssignmentCardAlignmentPills(
        alwaysExcludeStudentAssignmentCardAlignmentPills
      );
    }
    if ('avatarPickerFacultyName' in flagOverrides) {
      userManager.setAvatarPickerFacultyName(avatarPickerFacultyName);
    }
    if ('avatarPickerStudentName' in flagOverrides) {
      userManager.setAvatarPickerStudentName(avatarPickerStudentName);
    }
    if (Array.isArray(basicIframeModalLauncherUrlMapEntries)) {
      basicIframeModalLauncherManager.setBasicIframeModalLauncherUrlMap(basicIframeModalLauncherUrlMapEntries);
    }
    if (isBoolean(classWorkspaceFeature)) {
      appManager.classWorkspaceFeature = classWorkspaceFeature;
    }
    if (Array.isArray(classCardInfoOrderArray)) {
      classroomManager.setClassCardInfoOrderArray(classCardInfoOrderArray);
    }
    if (Array.isArray(courseTreeLeafButtonTypeOrderArray)) {
      courseManager.setCourseTreeLeafButtonTypeOrderArray(courseTreeLeafButtonTypeOrderArray);
    }
    if (typeof customEndpointsForGoogleClassroom === 'object') {
      googleClassroomManager.setCustomEndpointsForGoogleClassroom(customEndpointsForGoogleClassroom);
    }
    if (typeof customEndpointsForUser === 'object') {
      userManager.setCustomEndpointsForUser(customEndpointsForUser);
    }
    if (isBoolean(includeAlignments)) {
      assignmentManager.setIncludeAlignments(includeAlignments);
      courseManager.setIncludeAlignments(includeAlignments);
    }
    if (isBoolean(includeClassroomAccommodations)) {
      accommodationsManager.setIncludeClassroomAccommodations(includeClassroomAccommodations);
    }
    if (isBoolean(includeGroups)) {
      classroomManager.setIncludeGroups(includeGroups);
    }
    if (isBoolean(isUserProfileReadOnly)) {
      userManager.setIsUserProfileReadOnly(isUserProfileReadOnly);
    }
    if (typeof maxReportFacultyScoreCellsPerPage === 'number') {
      reportOverrideManager.setMaxReportFacultyScoreCellsPerPage(maxReportFacultyScoreCellsPerPage);
    }
    if (Array.isArray(reportContextTypeDefaultOrderArray)) {
      reportOverrideManager.setReportContextTypeDefaultOrderArray(reportContextTypeDefaultOrderArray);
      if (!getSessionStorageItem('reportContextType')) {
        // we do not have a defined 'selected' reportContextType in sessionStorage, so
        // set selected reportContextType to be whichever is first in provided array
        reportContextManager.setReportContextType(reportContextTypeDefaultOrderArray[0]);
      }
    }
    if (isBoolean(restrictAvatar)) {
      userManager.setRestrictAvatar(restrictAvatar);
    }
    if (isBoolean(restrictAvatarForAllUserTypes)) {
      userManager.setRestrictAvatarForAllUserTypes(restrictAvatarForAllUserTypes);
    }
    if (isBoolean(showGradebookKeyViewMoreModal)) {
      gradebookManager.setShowGradebookKeyViewMoreModal(showGradebookKeyViewMoreModal);
    }
    if (isBoolean(showResourcePacingToolbar)) {
      courseDeliveryManager.setShowResourcePacingToolbar(showResourcePacingToolbar);
    }
    if (Array.isArray(studentAssignmentMenuStatusOrderArray)) {
      assignmentManager.setStudentAssignmentMenuStatusOrderArray(studentAssignmentMenuStatusOrderArray);
    }
    if ('studentContentCardDateFormatStrategy' in flagOverrides) {
      studentContentCardManager.setStudentContentCardDateFormatStrategy(studentContentCardDateFormatStrategy);
    }
    if (Array.isArray(teacherClassroomCardFooterNavButtonOrderArray)) {
      classroomManager.setTeacherClassroomCardFooterNavButtonOrderArray(teacherClassroomCardFooterNavButtonOrderArray);
    }
    if (typeof testBuilderContextHelpId === 'string') {
      contextHelpManager.setTestBuilderContextHelpId(testBuilderContextHelpId);
    }
    if (typeof thirdPartyChatLauncherCustomVariables === 'object') {
      thirdPartyManager.setThirdPartyChatLauncherCustomVariables(thirdPartyChatLauncherCustomVariables);
    }
    if (typeof thirdPartyChatLauncherUrl === 'string') {
      thirdPartyManager.setThirdPartyChatLauncherUrl(thirdPartyChatLauncherUrl);
    }
    if (isBoolean(useProductLineLibrary)) {
      libraryManager.setUseProductLineLibrary(useProductLineLibrary);
    }
    if (isBoolean(wordToolsTTSDependent)) {
      accommodationsManager.setWordToolsTTSDependent(wordToolsTTSDependent);
    }
  };

  static initDynamicSatelliteTranslations = async ({ satelliteJsonUrl } = {}) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    const dynamicSatelliteTranslationResponse = satelliteJsonUrl ? await fetch(satelliteJsonUrl, {
      headers,
      method: 'GET',
      redirect: 'follow'
    }) : undefined;

    const dynamicSatelliteTranslations = await dynamicSatelliteTranslationResponse?.json?.();

    // merge the current dynamic satellite translations with the default sat-core translations
    i18nAddSatellite('dynamic', dynamicSatelliteTranslations || {});
  }

  static initDynamicSatelliteStyleSheet = ({ satelliteCssUrl } = {}) => {
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.type = 'text/css';

    cssLink.href = satelliteCssUrl;

    document.querySelector('head').appendChild(cssLink);
  }

  static initPostLoginSettersForDynamicSatellite = () => {
    // init satellite fav icon if it is not already set
    this.initDynamicFavIconIfApplicable();

    // set top nav 'Help/Resources' url for Student role if applicable
    // if (userManager.isStudent) {
    const cssUrlValue_topNavResourceUrlStudent = getStyleVar('--top-nav-dynamic-resource-url-student');
    const topNavResourceUrlStudent = getCssUrlArgument(cssUrlValue_topNavResourceUrlStudent);
    contentManager.setResourceIFrameUrlStudent(topNavResourceUrlStudent);
    // }

    // set top nav 'Help/Resources' url for non-Student role if applicable
    // if (!userManager.isStudent) {
    const cssUrlValue_topNavResourceUrl = getStyleVar('--top-nav-dynamic-resource-url');
    const topNavResourceUrl = getCssUrlArgument(cssUrlValue_topNavResourceUrl);
    contentManager.setResourceIFrameUrl(topNavResourceUrl);
    // }

    const cssUrlValue_topNavResourceUrlTarget = getStyleVar('--top-nav-dynamic-resource-url-target');
    const topNavResourceUrlTarget = getCssUrlArgument(cssUrlValue_topNavResourceUrlTarget);
    contentManager.setResourceIFrameUrlTarget(topNavResourceUrlTarget);

    // set useWhiteInfoIcon to true/false based on `fontColorDirectlyOnBackground`
    const fontColorOnBackground = userManager.isStudent ? (
      getStyleVar('--theme-font-color-directly-on-background-student')
    ) : (
      getStyleVar('--theme-font-color-directly-on-background')
    );
    const shouldUseWhiteInfoIcon = isWhiteStyleColor(fontColorOnBackground);
    dialogManager.setUseWhiteInfoIcon(shouldUseWhiteInfoIcon);
  }

  /**
   * Manually inject fav icon for a given satellite if it is not already in the DOM.
   *
   * Intended for dynamic satellites where the fav icon url is not hardcoded in `index.html`.
   *
   * If fav icon url is declared in `index.html` and never changes, no need to use this method. */
  static initDynamicFavIconIfApplicable = () => {
    try {
      let favIconLink = document.querySelector("link[rel~='icon']");

      const cssUrlValue = getStyleVar('--fav-icon');
      const favIconUrl = getCssUrlArgument(cssUrlValue);

      if (favIconLink?.href?.includes?.(favIconUrl)) {
        // we have already initialized the fav icon, so no need to run additional logic
        return;
      }

      favIconLink = document.createElement('link');
      favIconLink.rel = 'icon';
      document.head.appendChild(favIconLink);

      favIconLink.href = favIconUrl;
    } catch (error) {
      console.error(error);
    }
  }
}

registerClass('DynamicSatelliteService', DynamicSatelliteService);
